// src/components/site-editor/panels/HeroTextPanel.js
import React from 'react';
import { Lightbulb } from 'lucide-react';
import { useWebsiteBuilder } from '../../../contexts/WebsiteBuilderContext';
import { getPractitionerDisplayName } from '../../../utils/getPractitionerDisplayName';

const HeroTextPanel = () => {
  const { state, actions } = useWebsiteBuilder();
  const { draft } = state;

  const handleHeadlineChange = (e) => {
    actions.updateHero('headline', e.target.value);
  };

  const handleSubheadlineChange = (e) => {
    actions.updateHero('subheadline', e.target.value);
  };

  const handleAlignmentChange = (alignment) => {
    actions.updateHero('textAlign', alignment);
  };

  // Resolve the practitioner's display name from all available sources
  const displayName = getPractitionerDisplayName(state);

  const currentHeadline = draft.hero.headline || displayName || draft.profile.displayName || '';
  const currentSubheadline = draft.hero.subheadline || '';
  const headlineMatchesName = displayName &&
    currentHeadline.trim().toLowerCase() === displayName.trim().toLowerCase();

  return (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-1">Hero Text</h3>
        <p className="text-sm text-slate-600">Edit the text that appears in your hero section</p>
      </div>

      {/* Headline */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Headline
        </label>
        <input
          type="text"
          value={currentHeadline}
          onChange={handleHeadlineChange}
          placeholder="Your name or headline"
          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          maxLength={60}
        />
        <p className="text-xs text-slate-500 mt-1">{currentHeadline.length}/60 characters</p>

        {/* Contextual tip when headline matches display name */}
        {headlineMatchesName && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '6px',
              marginTop: '6px',
              padding: '8px 10px',
              background: '#FFFBEB',
              borderRadius: '6px',
              border: '1px solid #FEF3C7',
            }}
          >
            <Lightbulb size={14} color="#D97706" style={{ flexShrink: 0, marginTop: '1px' }} />
            <p style={{ fontSize: '12px', color: '#92400E', lineHeight: '1.4', margin: 0 }}>
              Your name already appears in the navigation bar. Consider using a tagline here instead, like "Guiding You Toward Clarity" or "Healing Through Intuitive Energy Work."
            </p>
          </div>
        )}
      </div>

      {/* Subheadline */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Sub-Headline
        </label>
        <textarea
          value={currentSubheadline}
          onChange={handleSubheadlineChange}
          placeholder="A short tagline about your practice"
          rows={3}
          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
          maxLength={150}
        />
        <p className="text-xs text-slate-500 mt-1">{currentSubheadline.length}/150 characters</p>
      </div>

      {/* Text Alignment */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-3">
          Text Alignment
        </label>
        <div className="grid grid-cols-3 gap-2">
          {['left', 'center', 'right'].map((align) => (
            <button
              key={align}
              onClick={() => handleAlignmentChange(align)}
              className={`px-4 py-2 rounded-lg border-2 transition-all text-sm font-medium capitalize ${
                draft.hero.textAlign === align
                  ? 'border-purple-600 bg-purple-50 text-purple-700'
                  : 'border-slate-200 hover:border-slate-300 text-slate-700'
              }`}
            >
              {align}
            </button>
          ))}
        </div>
      </div>

      {/* Writing Tips */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">Writing Tips</h4>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>• <strong>Headline:</strong> Your name or practice name works beautifully</li>
          <li>• <strong>Subheadline:</strong> Share what you offer or how you help</li>
          <li>• Keep it warm and welcoming, not salesy</li>
        </ul>
      </div>
    </div>
  );
};

export default HeroTextPanel;
